import { getApi, postApi, patchApi, ErrorResponse } from './baseApi';
import { UPLOAD_REQUEST_ENDPOINTS } from '../constants/endpoints';

// pending -> done (every ward uploaded, or marked done by the owner) | rejected | cancelled (taken back)
export type UploadRequestStatus = 'pending' | 'done' | 'rejected' | 'cancelled';

export type UploadRequestJobStatus = 'queued' | 'processing' | 'confirmed' | 'needs_review' | 'failed';

// One PDF the owner uploaded for a request
export interface UploadRequestJob {
  id: string;
  // The ward the PDF made (chapter 4)
  ward_id?: string | null;
  // That ward's state: ... | needs_review | confirmed
  ward_status?: string | null;
  // The ward the PDF turned out to be
  ward_number: string | null;
  filename: string | null;
  status: UploadRequestJobStatus;
  // Why it failed (status failed)
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface UploadRequest {
  id: string;
  status: UploadRequestStatus;
  created_at: string;
  resolved_at: string | null;
  // The campaign (the Admin); same id as /owner/clients/{admin_id}
  admin_id: string;
  admin_name: string | null;
  admin_mobile: string | null;
  // sarpanch | wardpanch | panchayat_samiti | zila_parishad; null = an older account
  admin_post: string | null;
  subscription_status: string | null;
  subscription_plan_id: string | null;
  requested_by: 'admin' | 'manager';
  manager_name: string | null;
  manager_mobile: string | null;
  // English place names, to search the SEC site
  district_name: string | null;
  // Hindi district name (new)
  district_name_hi: string | null;
  samiti_name: string | null;
  // Hindi samiti name (new)
  samiti_name_hi: string | null;
  // Location master rows (new)
  district_id: number | null;
  samiti_id: number | null;
  village_id: number | null;
  // Display only: the Hindi name when there is one, else the English one
  gram_panchayat: string;
  // English name; never blank
  gram_panchayat_en: string;
  // Every uploaded PDF's header is checked against this; null = the gram panchayat is not checked
  gram_panchayat_hi: string | null;
  ward_numbers: number[];
  // Requested wards already uploaded by the owner
  uploaded_wards: number[];
  note: string | null;
  reject_reason: string | null;
  // The owner's message when marking the request done (new)
  done_message: string | null;
  // Detail only: every PDF uploaded for this request, oldest first
  jobs?: UploadRequestJob[];
}

export interface UploadRequestsPage {
  total: number;
  limit: number;
  offset: number;
  items: UploadRequest[];
}

export interface UploadRequestListParams {
  status?: UploadRequestStatus;
  admin_id?: string;
  limit?: number;
  offset?: number;
}

// The menu badge listens for this, so it updates as soon as a request changes here
export const UPLOAD_REQUESTS_CHANGED_EVENT = 'upload-requests-changed';

export const notifyUploadRequestsChanged = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(UPLOAD_REQUESTS_CHANGED_EVENT));
};

export const SEC_PDF_DOWNLOAD_URL = 'https://sec.rajasthan.gov.in/se_pdfdownload.aspx';

export const getUploadRequestsApi = (params: UploadRequestListParams = {}): Promise<UploadRequestsPage> =>
  getApi(UPLOAD_REQUEST_ENDPOINTS.LIST, {
    ...(params.status && { status: params.status }),
    ...(params.admin_id && { admin_id: params.admin_id }),
    limit: Math.min(Math.max(1, params.limit ?? 50), 100),
    offset: Math.max(0, params.offset ?? 0),
  });

// For the menu badge: only the total matters
export const getPendingUploadRequestsCountApi = async (): Promise<number> => {
  const res: UploadRequestsPage = await getApi(UPLOAD_REQUEST_ENDPOINTS.LIST, { status: 'pending', limit: 1 });
  return res?.total ?? 0;
};

export const getUploadRequestApi = (id: string): Promise<UploadRequest> =>
  getApi(UPLOAD_REQUEST_ENDPOINTS.DETAILS(id));

// One PDF per call; can take up to about 2 minutes (the first page is read to find the ward). 202.
export const uploadRequestPdfApi = (id: string, file: File): Promise<UploadRequest> => {
  const form = new FormData();
  form.append('file', file);
  return postApi(UPLOAD_REQUEST_ENDPOINTS.UPLOAD(id), form);
};

// reason: 1-500 characters, shown to the requester in the app
export const rejectUploadRequestApi = (id: string, reason: string): Promise<UploadRequest | ErrorResponse> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.REJECT(id), { reason });

// Mark done early, e.g. a ward is not on the SEC site; optional done_message up to 500 characters
export const completeUploadRequestApi = (
  id: string,
  message?: string
): Promise<UploadRequest | ErrorResponse> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.COMPLETE(id), message ? { message: message.trim() } : {});

// Only the fields that changed; a field not sent stays as it is. null or '' clears it (not gram_panchayat_en).
export interface UploadRequestEditPayload {
  district_name?: string | null; // up to 150
  district_name_hi?: string | null; // up to 150
  samiti_name?: string | null; // up to 150
  samiti_name_hi?: string | null; // up to 150
  gram_panchayat_en?: string; // 1-255, 422 GRAM_PANCHAYAT_REQUIRED when blank
  gram_panchayat_hi?: string | null; // up to 255 (or 150 if update_master is true)
  ward_numbers?: number[]; // 1-100 items, each 1-100; replaces the whole list
  note?: string | null; // up to 500
  update_master?: boolean; // default true
  district_id?: number | null;
  samiti_id?: number | null;
  village_id?: number | null;
}

// Fix the place, wards or note of a pending request. 409 REQUEST_NOT_PENDING,
// 409 REQUEST_ALREADY_PENDING (extra.ward_numbers), 422 GRAM_PANCHAYAT_REQUIRED, 403 NOT_ALLOWED.
export const updateUploadRequestApi = (id: string, payload: UploadRequestEditPayload): Promise<UploadRequest> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.UPDATE(id), payload);

// ---------------------------------------------------------------------- Ward & Voter Review (Chapter 4)

export interface WardReviewGate {
  passed: boolean;
  flagged_voters: number;
  missing_gender: number;
  unmatched_supplement_changes: number;
  extracted_male: number;
  printed_male: number;
  extracted_female: number;
  printed_female: number;
  extracted_third_gender: number;
  printed_third_gender: number;
}

export interface ReviewVoter {
  id: string;
  serial_no: number;
  name: string;
  father_or_husband_name: string | null;
  epic_no: string | null;
  house_no: string | null;
  age: number | null;
  gender: string | null;
  mohalla: string | null;
  residency_status: string;
  roll_deleted: boolean;
  flagged_fields: string[];
  reasons: string[];
  note: string | null;
  held_back: boolean;
}

export interface WardReviewResponse {
  ward_id: string;
  ward_name: string;
  extraction_status: string;
  total: number;
  gate: WardReviewGate;
  voters: ReviewVoter[];
  locked_in_review: number;
  gram_panchayat: string | null;
}

export interface ReviewVoterFields {
  name?: string;
  father_or_husband_name?: string | null;
  house_no?: string | null;
  age?: number | string | null;
  gender?: string | null;
  mohalla?: string | null;
  epic_no?: string | null;
  residency_status?: ResidencyStatus;
}

export type ResidencyStatus = 'resides' | 'shifted' | 'deceased' | 'duplicate' | 'unverified';

export interface ReviewVoterResponse {
  voter: ReviewVoter | null;
  changed_fields: string[];
  ward_status: string;
  remaining: number;
  gate: WardReviewGate;
  locked_in_review: number;
}

export const getWardReviewApi = (
  requestId: string,
  wardId: string,
  params: { limit?: number; offset?: number } = {}
): Promise<WardReviewResponse> =>
  getApi(UPLOAD_REQUEST_ENDPOINTS.WARD_REVIEW(requestId, wardId), {
    limit: Math.min(Math.max(1, params.limit ?? 50), 200),
    offset: Math.max(0, params.offset ?? 0),
  });

export const reviewVoterApi = (
  requestId: string,
  wardId: string,
  voterId: string,
  fields: ReviewVoterFields
): Promise<ReviewVoterResponse | ErrorResponse> =>
  patchApi(UPLOAD_REQUEST_ENDPOINTS.VOTER_REVIEW(requestId, wardId, voterId), { fields });
